<template>
  <div v-loading="$wait.waiting(_uid)" class="card">
    <div class="card-content">
      <form ref="form" @submit.prevent="handleCreateUser">
        <div class="text-right control-button-wrapper pt-xxs">
          <router-link :to="{ name: 'companies_users@index' }">
            <button
              type="button"
              class="btn btn-cancel btn-primary btn-wd mr-xxs"
            >
              Cancel
            </button>
          </router-link>

          <acl :permissions="['profile_update']">
            <button
              type="submit"
              class="btn btn-info btn-fill btn-wd submit-button"
              :class="{ loading: $wait.waiting(_uid) }"
            >
              Create
              <i
                v-show="$wait.waiting(_uid)"
                class="fas fa-circle-notch loading-icon"
              />
            </button>
          </acl>
        </div>

        <div class="row">
          <div class="col-md-6">
            <fg-input
              v-model="model.name"
              type="text"
              required
              label="First Name"
              placeholder="First Name"
            />

            <fg-input
              v-model="model.surname"
              type="text"
              required
              label="Last Name"
              placeholder="Last Name"
            />
          </div>

          <div class="col-md-6">
            <fg-input
              v-model="model.username"
              type="email"
              required
              label="Username"
              placeholder="Username"
            />
          </div>
        </div>

        <div class="row mb-s pb-s">
          <div class="card-header">
            <h4 class="title">User Type(s)</h4>
          </div>

          <div class="col-md-6 col-xs-12">
            <div v-if="operational.externalAcl.length">
              <div
                v-for="acl in operational.externalAcl"
                :key="acl.id + ':key'"
                class="form-group"
              >
                <p-switch
                  :value="model.acls[acl.id].active"
                  type="primary"
                  class="mr-xs"
                  @input="toggleAcl(acl)"
                />
                <label>{{ acl.name }}</label>
              </div>
            </div>

            <div v-if="operational.internalAcl.length">
              <hr />
              <div
                v-for="acl in operational.internalAcl"
                :key="acl.id + ':key'"
                class="form-group"
              >
                <p-switch
                  :value="model.acls[acl.id].active"
                  type="primary"
                  class="mr-xs"
                  @input="toggleAcl(acl)"
                />
                <label>{{ acl.name }}</label>
              </div>
            </div>
          </div>
        </div>

        <hr />

        <div v-if="model.isTrainee">
          <div class="row mb-s pb-s">
            <div class="card-header">
              <h4 class="title">Operational Information</h4>
            </div>

            <div class="col-md-4 col-xs-12">
              <h5 class="title">Registry, Purpose, Equipment</h5>
              <div
                v-for="rpe in operational.rpes"
                :key="rpe.id"
                class="form-group"
              >
                <p-switch
                  v-model="model.companyRpes[rpe.id].active"
                  type="primary"
                  class="mr-xs"
                />
                <label>{{ rpe.name }}</label>
              </div>
            </div>

            <div class="col-md-4 col-xs-12">
              <h5 class="title">Role(s)</h5>
              <div
                v-for="role in operational.roles"
                :key="role.id"
                class="form-group"
              >
                <p-switch
                  v-model="model.companyRoles[role.id].active"
                  type="primary"
                  class="mr-xs"
                />
                <label>{{ role.name }}</label>
                <fg-input
                  v-show="model.companyRoles[role.id].active"
                  v-model="model.companyRoles[role.id].licence"
                  type="text"
                  placeholder="Licence"
                  class="mt-xxs"
                />
              </div>
            </div>

            <div class="col-md-4 col-xs-12">
              <h5 class="title">Hemisphere</h5>
              <div class="form-group">
                <p-switch
                  :value="model.hemisphere === 'N'"
                  type="primary"
                  class="mr-xs"
                  @input="setHemisphere('N')"
                />
                <label>Northern</label>
              </div>

              <div class="form-group">
                <p-switch
                  :value="model.hemisphere === 'S'"
                  type="primary"
                  class="mr-xs"
                  @input="setHemisphere('S')"
                />
                <label>Southern</label>
              </div>
            </div>

            <div class="col-xs-12 mt-s">
              <label class="mr-xs">Subscription Renewal</label>
              <el-date-picker
                v-model="model.subscription"
                type="date"
                placeholder="Pick a day"
                :editable="false"
              />
            </div>
          </div>
          <hr />
        </div>

        <div class="row mb-s pb-s">
          <div class="card-header">
            <h4 class="title">Settings</h4>
          </div>

          <div class="col-md-6 col-xs-12">
            <fg-input
              v-model="model.email"
              type="email"
              required
              label="Email"
              placeholder="Email"
            />
            <div class="form-group">
              <p-switch
                v-model="model.settings.notifications"
                type="primary"
                class="mr-xs"
              />
              <label>Notifications</label>
            </div>
          </div>
        </div>

        <div class="clearfix mb-s" />
      </form>
    </div>
  </div>
</template>
<script>
import HelperDate from "@helpers/dates";
import PSwitch from "@components/UIComponents/Switch.vue";
import { DatePicker } from "element-ui";
import _get from "lodash.get";
import arrToObj from "@helpers/arrToObj";
import addPropToObj from "@helpers/addPropToObj";
import { mapActions, mapGetters, mapMutations } from "vuex";
import toggleAcl from "./providers/toggleAcl";
import setHemisphere from "./providers/setHemisphere";
import getModelOperational from "./providers/getModelOperational";
import checkRpeAndRole from "./providers/checkRpeAndRole";
import parseAcl from "./providers/parseAcl";

export default {
  components: {
    PSwitch,
    [DatePicker.name]: DatePicker,
  },
  data() {
    const model = {
      name: "",
      surname: "",
      email: "",
      username: "",
      hemisphere: "N",
      companyRpes: {},
      companyRoles: {},
      subscription: HelperDate.formatDate(this.moment()),
      acls: {},

      settings: {
        notifications: false,
      },
    };

    return {
      model,
    };
  },
  computed: {
    ...mapGetters("loadingStore", { isLoading: "loading" }),
    ...mapGetters("companyUserStore", { storedOperational: "operational" }),
    operational() {
      [this.addRpes, this.addRoles, this.addAcl].forEach((fn) =>
        fn(this.storedOperational)
      );

      const operationalAcl = this.storedOperational.acl || [];

      return {
        ...this.storedOperational,
        ...parseAcl(operationalAcl),
      };
    },
  },
  async mounted() {
    try {
      this.$wait.start(this._uid);
      await this.getCompanyOperationalInformation();
    } catch (error) {
      this.addNewError(error);
    } finally {
      this.$wait.end(this._uid);
    }
  },
  methods: {
    ...mapActions("errorStore", ["addNewError"]),
    ...mapActions("companyUserStore", [
      "createCompanyUser",
      "getCompanyOperationalInformation",
    ]),
    ...mapMutations("loadingStore", ["setLoading"]),
    toggleAcl,
    setHemisphere,
    getModelOperational,
    checkRpeAndRole,
    addRpes(operational) {
      const rpes = _get(operational, "rpes");
      if (!rpes) {
        return;
      }

      const parsedRpes = rpes.map((rpe) => addPropToObj(rpe, "active", false));

      this.model.companyRpes = arrToObj(parsedRpes);
    },
    addRoles(operational) {
      const roles = _get(operational, "roles");
      if (!roles) {
        return;
      }

      const parsedRoles = roles
        .map((role) => addPropToObj(role, "active", false))
        .map((role) => addPropToObj(role, "licence", ""));

      this.model.companyRoles = arrToObj(parsedRoles);
    },
    addAcl(operational) {
      const acls = _get(operational, "acl");
      if (!acls) {
        return;
      }

      const parsedAcls = acls.map((role) =>
        addPropToObj(role, "active", false)
      );

      this.model.acls = arrToObj(parsedAcls);
    },
    _getIdAndLicence(list) {
      return list.map((option) =>
        "licence" in option
          ? { id: option.id, licence: option.licence }
          : option.id
      );
    },
    _getValuesFromDataModel(keys) {
      return keys.reduce(
        (acc, prop) => ({
          ...acc,
          [prop]: this.model[prop],
        }),
        {}
      );
    },
    getApiModel({ roles, companyRpes, companyRoles }) {
      const apiModel = {
        roles,
        ...this._getValuesFromDataModel([
          "name",
          "surname",
          "email",
          "username",
          "settings",
        ]),
      };

      if (this.model.isTrainee) {
        Object.assign(apiModel, {
          companyRpes,
          companyRoles,
          subscription: HelperDate.formatDate(this.model.subscription),
          ...this._getValuesFromDataModel(["hemisphere"]),
        });
      }

      return apiModel;
    },
    async handleCreateUser() {
      const [companyRpes, roles, companyRoles] = this.getModelOperational();

      const isValid = this.checkRpeAndRole(companyRpes, companyRoles);
      if (!isValid) {
        return;
      }

      const apiModel = this.getApiModel({ roles, companyRpes, companyRoles });

      try {
        await this.createCompanyUser(apiModel);

        this.$notify({
          message: "Data updated successfully!",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "success",
        });

        setTimeout(
          () => this.$router.push({ name: "companies_users@index" }),
          300
        );
      } catch (error) {
        this.addNewError(error);
      }
    },
  },
};
</script>
